import { useRef, useEffect } from 'react'
import * as THREE from 'three'
/**
 * HeroSphere — interactive Three.js sphere in the hero right column.
 * Click to cycle through 4 colour modes (cyan → violet → amber → emerald).
 * Tilts toward the mouse cursor in real time.
 */
export function HeroSphere() {
  const ref = useRef()
  useEffect(() => {
    const canvas = ref.current
    const R = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true })
    R.setPixelRatio(Math.min(devicePixelRatio, 2)); R.setClearColor(0, 0)
    const S = new THREE.Scene()
    const C = new THREE.PerspectiveCamera(48, 1, 0.1, 100); C.position.z = 5.2
    const resize = () => { const w = canvas.clientWidth || 420, h = canvas.clientHeight || w; R.setSize(w, h, false); C.aspect = w / h; C.updateProjectionMatrix() }
    resize()

    const MODES = [
      { sc: 0x00d4ff, ic: 0x7c3aed, rc: 0x00d4ff },
      { sc: 0x7c3aed, ic: 0xf0abfc, rc: 0xa78bfa },
      { sc: 0xf59e0b, ic: 0x00d4ff, rc: 0xf59e0b },
      { sc: 0x10b981, ic: 0x7c3aed, rc: 0x10b981 },
    ]
    let mode = 0
    const sph = new THREE.Mesh(new THREE.SphereGeometry(2, 20, 20),   new THREE.MeshBasicMaterial({ color: MODES[0].sc, wireframe: true, transparent: true, opacity: .28 })); S.add(sph)
    const inn = new THREE.Mesh(new THREE.IcosahedronGeometry(1.25, 1), new THREE.MeshBasicMaterial({ color: MODES[0].ic, wireframe: true, transparent: true, opacity: .55 })); S.add(inn)
    const mkR = (r, op, rx = 0, ry = 0) => { const m = new THREE.Mesh(new THREE.TorusGeometry(r, .02, 8, 90), new THREE.MeshBasicMaterial({ color: MODES[0].rc, transparent: true, opacity: op })); m.rotation.x = rx; m.rotation.y = ry; S.add(m); return m }
    const r1 = mkR(2.55, .65, Math.PI / 2.8), r2 = mkR(2.3, .5, -Math.PI / 3.5, Math.PI / 5), r3 = mkR(2.75, .22)

    const dc = 64, dp = new Float32Array(dc * 3), phi = Math.PI * (3 - Math.sqrt(5))
    for (let i = 0; i < dc; i++) { const y = 1 - (i / (dc - 1)) * 2, rr = Math.sqrt(1 - y * y), th = phi * i; dp[i * 3] = Math.cos(th) * rr * 2.75; dp[i * 3 + 1] = y * 2.75; dp[i * 3 + 2] = Math.sin(th) * rr * 2.75 }
    const dG = new THREE.BufferGeometry(); dG.setAttribute('position', new THREE.BufferAttribute(dp, 3))
    const dM = new THREE.PointsMaterial({ color: 0x00d4ff, size: .07, transparent: true, opacity: .9 }); S.add(new THREE.Points(dG, dM))

    let pulsing = false, pulseT = 0
    const onClick = () => {
      mode = (mode + 1) % MODES.length; const m = MODES[mode]
      sph.material.color.setHex(m.sc); inn.material.color.setHex(m.ic)
      ;[r1, r2, r3].forEach(r => r.material.color.setHex(m.rc)); dM.color.setHex(m.sc)
      pulsing = true; pulseT = 0
    }
    canvas.addEventListener('click', onClick)
    const mouse = { x: 0, y: 0 }
    const onMouse = e => { const rc = canvas.getBoundingClientRect(); if (e.clientX >= rc.left && e.clientX <= rc.right && e.clientY >= rc.top && e.clientY <= rc.bottom) { mouse.x = ((e.clientX - rc.left) / rc.width - .5) * 2; mouse.y = -((e.clientY - rc.top) / rc.height - .5) * 2 } }
    document.addEventListener('mousemove', onMouse)
    const onR = () => requestAnimationFrame(resize)
    window.addEventListener('resize', onR)

    let t = 0, aid
    const animate = () => {
      aid = requestAnimationFrame(animate); t += .012
      let sc = 1; if (pulsing) { pulseT += .08; sc = 1 + Math.sin(pulseT) * .08 * Math.exp(-pulseT * .6); if (pulseT > 4) pulsing = false }
      S.scale.setScalar(sc)
      sph.rotation.y = t * .28 + mouse.x * .55; sph.rotation.x = mouse.y * .28 + Math.sin(t * .18) * .12
      inn.rotation.y = -t * .42; inn.rotation.x = t * .28 + mouse.y * .2
      r1.rotation.z = t * .18; r2.rotation.z = -t * .14; r3.rotation.y = t * .22
      R.render(S, C)
    }
    animate()
    return () => { cancelAnimationFrame(aid); canvas.removeEventListener('click', onClick); document.removeEventListener('mousemove', onMouse); window.removeEventListener('resize', onR); R.dispose() }
  }, [])
  return <canvas ref={ref} className="hero-canvas" title="Click to change colour mode" />
}
